import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Paper,
  Tooltip,
  SwitchProps,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, ExpandLess, SaveOutlined } from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import API from '@Services/apiAxios';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';

interface Notification {
  module: string;
  triggers: string[];
}

interface DeliveryMethod {
  sms: boolean;
  email: boolean;
  dashboard: boolean;
}

interface Recipient {
  customer: boolean;
  shipper: boolean;
  consignee: boolean;
  admin: boolean;
}

interface NotificationSettings {
  trigger: string;
  deliveryMethods: DeliveryMethod;
  recipients: Recipient;
}

const modules: Notification[] = [
  {
    module: 'User Status Notifications',
    triggers: ['Invitation Sent', 'Kyc Pending', 'Active', 'Kyc Verification', 'Suspended', 'Deactivated'],
  },
  {
    module: 'Shipment Status Notifications',
    triggers: [
      'Placed',
      'At Warehouse',
      'Forwarded',
      'In Transit',
      'Shipment Exception',
      'Rto',
      'Cancelled',
      'Delivered',
    ],
  },
  {
    module: 'Shipment Changes Notifications',
    triggers: ['Weight Change', 'Address Change', 'Price Change', 'Amount Refunded'],
  },
  {
    module: 'Invoice Notifications',
    triggers: [
      'Invoice Created',
      'Credit Note Created',
      'Debit Note Created',
      'Invoice Due Date Reached',
      'Wallet Updated',
      'Credit Limit Updated',
      'Current Credit Utilization 50%',
      'Current Credit Utilization 70%',
      'Current Credit Utilization 90%',
      'Current Credit Utilization 100%',
    ],
  },
  {
    module: 'Pricing Notifications',
    triggers: ['Change Base Price', 'Change Tariff Price', 'Change Contract Price', 'Oda Updation'],
  },
];

const prefilledSettings: Record<string, NotificationSettings> = {
  'Invitation Sent': {
    trigger: 'Invitation Sent',
    deliveryMethods: { sms: false, email: false, dashboard: false },
    recipients: {
      customer: false,
      shipper: false,
      consignee: false,
      admin: false,
    },
  },
  Placed: {
    trigger: 'Placed',
    deliveryMethods: { sms: false, email: false, dashboard: false },
    recipients: {
      customer: false,
      shipper: false,
      consignee: false,
      admin: false,
    },
  },
};

const transformApiResponse = (data: any[]) => {
  const transformed: Record<string, NotificationSettings> = {};

  data.forEach(item => {
    const [, triggerName] = item.code.split('.');

    const formattedTrigger = triggerName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char: string) => char.toUpperCase())
      .replace(/\s%/g, '%');

    const formattedKey = formattedTrigger;

    transformed[formattedKey] = {
      trigger: formattedKey,
      deliveryMethods: {
        sms: item.sms,
        email: item.email,
        dashboard: item.dashboard,
      },
      recipients: {
        customer: item.toCustomer,
        shipper: item.toShipper,
        consignee: item.toConsignee,
        admin: item.toAdmin,
      },
    };
  });

  return transformed;
};

// ===== STYLED COMPONENTS =====

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 24px',
  minHeight: '64px',
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  transition: 'all 0.25s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const ChildRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0 24px 0 84px',
  minHeight: '64px',
  background: alpha(theme.palette.primary.main, 0.02),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
  transition: 'all 0.25s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
}));

const Info = styled(Box)(() => ({
  flex: '1 1 300px', // 👈 fixed left column width consistency
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minHeight: '64px',
}));

const Col = styled(Box)(() => ({
  flex: '0 0 120px', // 👈 fixed width ensures perfect alignment
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '56px',
}));

const Container = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  overflow: 'hidden',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: alpha(theme.palette.background.paper, 0.75),
  backdropFilter: 'blur(14px)',
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '0 24px',
  minHeight: '64px',
  alignItems: 'center',
  background: alpha(theme.palette.primary.main, 0.05),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  fontWeight: 600,
}));

interface IndeterminateSwitchProps extends SwitchProps {
  indeterminate?: boolean;
}

const StyledIndeterminateSwitch = styled((props: IndeterminateSwitchProps) => {
  const { indeterminate, ...rest } = props;
  return (
    <Switch
      {...rest}
      checked={indeterminate ? false : rest.checked}
      sx={{
        ...(indeterminate && {
          '& .MuiSwitch-switchBase': {
            color: theme => theme.palette.secondary.main,
          },
          '& .MuiSwitch-track': {
            backgroundColor: theme => alpha(theme.palette.secondary.main, 0.5),
            opacity: 1,
          },
        }),
      }}
    />
  );
})(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.primary.light,
    opacity: 1,
  },
  '& .MuiSwitch-track': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.3),
  },
  '& .MuiSwitch-switchBase': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: '#fff',
  padding: '12px 32px',
  borderRadius: '12px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '16px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
  },
  '&:disabled': {
    background: alpha(theme.palette.primary.main, 0.3),
    color: theme.palette.primary.contrastText,
    opacity: 0.5,
  },
  transition: 'all 0.3s ease',
}));

const CleanCollapseWrapper = styled(Box)({
  border: 'none',
  boxShadow: 'none',
});

const ChildGroup = styled(Box)(({ theme }) => ({
  border: 'none',
  background: alpha(theme.palette.background.paper, 0.3),
}));

// ===== MAIN COMPONENT =====

const NotificationSettingsUI: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [settings, setSettings] = useState<Record<string, NotificationSettings>>(prefilledSettings);
  const [originalSettings, setOriginalSettings] = useState<Record<string, NotificationSettings>>(prefilledSettings);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });
  const orgCountryObj = useSelector((state: RootStateType) => state?.orgSetup?.organizationData);

  const disableSms = orgCountryObj?.name?.toLowerCase() !== 'india';

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  }, [settings, originalSettings]);

  const isRecipientDisabled = useCallback((moduleName: string, recipient: keyof Recipient) => {
    switch (moduleName) {
      case 'User Status Notifications':
        return ['shipper', 'admin', 'consignee'].includes(recipient);
      case 'Shipment Status Notifications':
        return recipient === 'admin';
      case 'Shipment Changes Notifications':
        return recipient === 'admin';
      case 'Invoice Notifications':
        return ['shipper', 'admin', 'consignee'].includes(recipient);
      case 'Pricing Notifications':
        return ['shipper', 'admin', 'consignee'].includes(recipient);
      default:
        return false;
    }
  }, []);

  const isDashboardDisabled = useCallback((moduleName: string) => {
    switch (moduleName) {
      case 'User Status Notifications':
      case 'Shipment Changes Notifications':
      case 'Pricing Notifications':
        return true;
      default:
        return false;
    }
  }, []);

  const isEmailDisabled = useCallback((moduleName: string, trigger: string) => {
    if (moduleName === 'Pricing Notifications') {
      return ['Change Base Price', 'Change Tariff Price', 'Oda Updation'].includes(trigger);
    }
    return false;
  }, []);

  const isCustomerDisabled = useCallback(
    (moduleName: string, trigger: string) => {
      if (moduleName === 'Pricing Notifications') {
        return ['Change Base Price', 'Change Tariff Price', 'Oda Updation'].includes(trigger);
      }
      return isRecipientDisabled(moduleName, 'customer');
    },
    [isRecipientDisabled],
  );

  const isShipperDisabled = useCallback(
    (moduleName: string, trigger: string) => {
      if (moduleName === 'Shipment Changes Notifications' && trigger === 'Amount Refunded') {
        return true;
      }
      return isRecipientDisabled(moduleName, 'shipper');
    },
    [isRecipientDisabled],
  );

  const isConsigneeDisabled = useCallback(
    (moduleName: string, trigger: string) => {
      if (moduleName === 'Shipment Changes Notifications' && trigger === 'Amount Refunded') {
        return true;
      }
      return isRecipientDisabled(moduleName, 'consignee');
    },
    [isRecipientDisabled],
  );

  // Calculate indeterminate state for module
  const isModuleIndeterminate = useCallback(
    (module: Notification) => {
      const triggerStates = module.triggers.map(trigger => {
        const settings_obj = settings[trigger];
        return {
          delivery:
            settings_obj?.deliveryMethods?.email ||
            settings_obj?.deliveryMethods?.dashboard ||
            (!disableSms && settings_obj?.deliveryMethods?.sms),
          recipients: Object.values(settings_obj?.recipients || {}).some(v => v),
        };
      });

      const anyEnabled = triggerStates.some(s => s.delivery || s.recipients);
      const allEnabled = triggerStates.every(s => s.delivery && s.recipients);

      return anyEnabled && !allEnabled;
    },
    [settings, disableSms],
  );

  // Check if all triggers in module are enabled
  const isModuleAllEnabled = useCallback(
    (module: Notification) => {
      return module.triggers.every(trigger => {
        const settings_obj = settings[trigger];
        const hasDelivery =
          settings_obj?.deliveryMethods?.email ||
          settings_obj?.deliveryMethods?.dashboard ||
          (!disableSms && settings_obj?.deliveryMethods?.sms);
        const hasRecipients = Object.values(settings_obj?.recipients || {}).some(v => v);
        return hasDelivery && hasRecipients;
      });
    },
    [settings, disableSms],
  );

  const fetchConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await API.get('/notification/getAllConfig');
      if (response?.status === 200) {
        const transformedSettings = transformApiResponse(response.data.data);
        setSettings(transformedSettings);
        setOriginalSettings(transformedSettings);
      }
    } catch (error) {
      console.error('Error fetching configs:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load notification settings',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleCheckboxChange = useCallback(
    (
      trigger: string,
      type: 'deliveryMethods' | 'recipients',
      key: keyof DeliveryMethod | keyof Recipient,
      checked: boolean,
      moduleName: string,
    ) => {
      if (key === 'sms' && disableSms) return;
      if (key === 'dashboard' && isDashboardDisabled(moduleName)) return;
      if (key === 'email' && isEmailDisabled(moduleName, trigger)) return;
      if (type === 'recipients' && key === 'customer' && isCustomerDisabled(moduleName, trigger)) return;
      if (type === 'recipients' && key === 'shipper' && isShipperDisabled(moduleName, trigger)) return;
      if (type === 'recipients' && key === 'consignee' && isConsigneeDisabled(moduleName, trigger)) return;
      if (type === 'recipients' && isRecipientDisabled(moduleName, key as keyof Recipient)) return;

      setSettings(prev => ({
        ...prev,
        [trigger]: {
          ...prev[trigger],
          [type]: {
            ...prev[trigger]?.[type],
            [key]: checked,
          },
        },
      }));
    },
    [
      disableSms,
      isDashboardDisabled,
      isEmailDisabled,
      isCustomerDisabled,
      isShipperDisabled,
      isConsigneeDisabled,
      isRecipientDisabled,
    ],
  );

  const getCompleteSettings = useCallback(() => {
    const configs: any[] = [];

    modules.forEach(module => {
      const moduleName = module.module.toLowerCase().replace(/\s+/g, '_');

      module.triggers.forEach(trigger => {
        const triggerName = trigger.toLowerCase().replace(/\s+/g, '_');

        const existing = settings[trigger] || {
          trigger,
          deliveryMethods: { sms: false, email: false, dashboard: false },
          recipients: { customer: false, shipper: false, consignee: false, admin: false },
        };

        configs.push({
          code: `${moduleName}.${triggerName}`,
          sms: existing.deliveryMethods.sms || false,
          email: existing.deliveryMethods.email || false,
          dashboard: existing.deliveryMethods.dashboard || false,
          toCustomer: existing?.recipients?.customer || false,
          toShipper: existing?.recipients?.shipper || false,
          toConsignee: existing?.recipients?.consignee || false,
          toAdmin: existing?.recipients?.admin || false,
        });
      });
    });

    return { configs };
  }, [settings]);

  const handleSubmit = useCallback(async () => {
    const completeSettings = getCompleteSettings();
    try {
      setIsSaving(true);
      const response = await API.post('/notification/updateConfig', { configs: completeSettings?.configs });
      if (response?.status === 200) {
        setOriginalSettings(settings);
        dispatch(
          SuccessErrorModalOpen({
            type: 'success',
            title: 'Success',
            subTitle: response?.data?.msg,
          }),
        );
        setSnackbar({
          open: true,
          message: 'Notification settings saved successfully!',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save notification settings',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [getCompleteSettings, settings, dispatch]);

  const toggleModule = useCallback((module: string) => {
    setExpanded(prev => ({
      ...prev,
      [module]: !prev[module],
    }));
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box>
        {/* Header */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontSize: '24px', fontWeight: 700 }} gutterBottom>
              Notification Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage notification triggers, delivery methods, and recipients
            </Typography>
            {hasChanges && (
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'secondary.main',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                <Typography variant="caption" color="warning.main" fontWeight={500}>
                  You have unsaved changes
                </Typography>
              </Box>
            )}
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Tooltip title={hasChanges ? 'Save all changes' : 'No changes to save'}>
              <span>
                <SaveButton
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveOutlined />}
                  onClick={handleSubmit}
                  disabled={isSaving || !hasChanges}
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </SaveButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Container */}
        <Container>
          {modules.map(module => {
            const isOpen = expanded[module.module];
            const isIndeterminate = isModuleIndeterminate(module);
            const isAllEnabled = isModuleAllEnabled(module);

            return (
              <Box key={module.module}>
                {/* MODULE ROW */}
                <Row
                  sx={{
                    backgroundColor: isOpen || isAllEnabled ? theme => alpha(theme.palette.primary.main, 0.02) : 'none',
                  }}
                >
                  <Info onClick={() => toggleModule(module.module)} sx={{ cursor: 'pointer' }}>
                    <Tooltip title={isOpen ? 'Collapse module' : 'Expand module'}>
                      <IconButton
                        size="small"
                        sx={{
                          opacity: 0.8,
                        }}
                      >
                        {isOpen ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Tooltip>

                    <Typography fontWeight={600} sx={{ fontSize: '15px' }}>
                      {module.module}
                    </Typography>

                    {isIndeterminate && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: 'secondary.main',
                          ml: 1,
                        }}
                      />
                    )}
                  </Info>

                  <Col />
                  <Col />
                  <Col />
                  <Col />
                  <Col />
                  <Col />
                </Row>

                {/* COLLAPSE CONTENT */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <CleanCollapseWrapper>
                    {/* HEADER ROW */}
                    <HeaderRow>
                      <Info>
                        <Typography fontWeight={700}>Trigger</Typography>
                      </Info>

                      {/* <Col>SMS</Col> */}
                      <Col>Email</Col>
                      <Col>Dashboard</Col>
                      <Col>Customer</Col>
                      <Col>Shipper</Col>
                      <Col>Consignee</Col>
                    </HeaderRow>

                    {/* CHILD GROUP */}
                    <ChildGroup>
                      {module.triggers.map(trigger => (
                        <ChildRow key={trigger}>
                          <Info>
                            <Typography fontSize={13} color="text.secondary" fontWeight={500}>
                              {trigger}
                            </Typography>
                          </Info>

                          {/* SMS */}
                          {/* <Col>
                            <Tooltip title={disableSms ? 'SMS not available in your region' : 'Enable SMS delivery'}>
                              <Box sx={{ opacity: disableSms ? 0.4 : 1 }}>
                                <StyledIndeterminateSwitch
                                  disabled={disableSms}
                                  checked={settings[trigger]?.deliveryMethods?.sms || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'deliveryMethods',
                                      'sms',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col> */}

                          {/* EMAIL */}
                          <Col>
                            <Tooltip
                              title={
                                isEmailDisabled(module.module, trigger)
                                  ? 'Email disabled for this trigger'
                                  : 'Enable email delivery'
                              }
                            >
                              <Box
                                sx={{
                                  opacity: isEmailDisabled(module.module, trigger) ? 0.4 : 1,
                                }}
                              >
                                <StyledIndeterminateSwitch
                                  disabled={isEmailDisabled(module.module, trigger)}
                                  checked={settings[trigger]?.deliveryMethods?.email || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'deliveryMethods',
                                      'email',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col>

                          {/* DASHBOARD */}
                          <Col>
                            <Tooltip
                              title={
                                isDashboardDisabled(module.module)
                                  ? 'Dashboard notifications not available for this module'
                                  : 'Enable dashboard notification'
                              }
                            >
                              <Box
                                sx={{
                                  opacity: isDashboardDisabled(module.module) ? 0.4 : 1,
                                }}
                              >
                                <StyledIndeterminateSwitch
                                  disabled={isDashboardDisabled(module.module)}
                                  checked={settings[trigger]?.deliveryMethods?.dashboard || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'deliveryMethods',
                                      'dashboard',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col>

                          {/* CUSTOMER */}
                          <Col>
                            <Tooltip
                              title={
                                isCustomerDisabled(module.module, trigger)
                                  ? 'Customer recipient not available for this trigger'
                                  : 'Send to customer'
                              }
                            >
                              <Box sx={{ opacity: isCustomerDisabled(module.module, trigger) ? 0.4 : 1 }}>
                                <StyledIndeterminateSwitch
                                  disabled={isCustomerDisabled(module.module, trigger)}
                                  checked={settings[trigger]?.recipients?.customer || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'recipients',
                                      'customer',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col>

                          {/* SHIPPER */}
                          <Col>
                            <Tooltip
                              title={
                                isShipperDisabled(module.module, trigger)
                                  ? 'Shipper recipient not available for this trigger'
                                  : 'Send to shipper'
                              }
                            >
                              <Box sx={{ opacity: isShipperDisabled(module.module, trigger) ? 0.4 : 1 }}>
                                <StyledIndeterminateSwitch
                                  disabled={isShipperDisabled(module.module, trigger)}
                                  checked={settings[trigger]?.recipients?.shipper || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'recipients',
                                      'shipper',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col>

                          {/* CONSIGNEE */}
                          <Col>
                            <Tooltip
                              title={
                                isConsigneeDisabled(module.module, trigger)
                                  ? 'Consignee recipient not available for this trigger'
                                  : 'Send to consignee'
                              }
                            >
                              <Box sx={{ opacity: isConsigneeDisabled(module.module, trigger) ? 0.4 : 1 }}>
                                <StyledIndeterminateSwitch
                                  disabled={isConsigneeDisabled(module.module, trigger)}
                                  checked={settings[trigger]?.recipients?.consignee || false}
                                  onChange={e =>
                                    handleCheckboxChange(
                                      trigger,
                                      'recipients',
                                      'consignee',
                                      e.target.checked,
                                      module.module,
                                    )
                                  }
                                  size="small"
                                />
                              </Box>
                            </Tooltip>
                          </Col>
                        </ChildRow>
                      ))}
                    </ChildGroup>
                  </CleanCollapseWrapper>
                </Collapse>
              </Box>
            );
          })}
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{
            width: '100%',
            bgcolor: snackbar.severity === 'success' ? 'primary.main' : undefined,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationSettingsUI;
